import type { RoomCommand, RoomProjection } from "@quizshow/protocol";

export function QuestionToolbar({
  projection,
  fiftyFiftyComplete,
  command,
  onPrevious,
  onReset,
}: {
  projection: RoomProjection;
  fiftyFiftyComplete: boolean;
  command: (command: RoomCommand) => void;
  onPrevious: () => void;
  onReset: () => void;
}) {
  const selectedCellId = projection.board?.selectedCellId;
  return (
    <div className="question-toolbar" aria-label="Fragensteuerung">
      <div className="question-toolbar__primary" data-toolbar-group="primary">
        <button
          className="button button--accent"
          disabled={!projection.question}
          onClick={() =>
            command({
              type: "SHOW_SOLUTION",
              releasedToParticipants: true,
              releasedToStream: true,
            })
          }
        >
          Lösung anzeigen
        </button>
        <button
          className="button button--ghost"
          disabled={!projection.question || projection.questionResolution.resolved}
          title="Beendet die Frage und zeigt die Lösung, vergibt aber keine zusätzliche Wertung."
          onClick={() =>
            command({
              type: "RESOLVE_QUESTION",
              releasedToParticipants: true,
              releasedToStream: true,
            })
          }
        >
          Frage auflösen
        </button>
        {projection.moduleId !== "quizboard" ? (
          <button
            className="button button--primary"
            disabled={projection.status !== "running"}
            onClick={() => command({ type: "NEXT_QUESTION" })}
          >
            Nächste Frage
          </button>
        ) : selectedCellId ? (
          <button
            className="button button--primary"
            onClick={() => command({ type: "MARK_BOARD_CELL_USED", cellId: selectedCellId })}
          >
            Feld abschließen
          </button>
        ) : null}
        <button
          className="button button--ghost"
          disabled={projection.moduleId !== "quizboard" && projection.currentQuestionIndex <= 0}
          onClick={
            projection.moduleId === "quizboard"
              ? () => command({ type: "RETURN_TO_BOARD" })
              : onPrevious
          }
        >
          {projection.moduleId === "quizboard" ? "Zurück zum Board" : "Vorherige Frage"}
        </button>
        <button
          className="button button--primary"
          disabled={!projection.question || projection.buzzer.status === "open"}
          onClick={() => command({ type: "OPEN_BUZZER" })}
        >
          Buzzer freigeben
        </button>
        <button
          className="button button--ghost"
          disabled={!projection.question || projection.buzzer.status !== "open"}
          onClick={() => command({ type: "CLOSE_BUZZER" })}
        >
          Buzzer sperren
        </button>
      </div>

      <details className="question-toolbar__menu">
        <summary className="button button--small button--ghost">Weitere Aktionen</summary>
        <div className="question-toolbar__panel">
          <div data-toolbar-group="question">
            <span className="eyebrow">Frage</span>
            <button
              disabled={!projection.question}
              onClick={() => command({ type: "SHOW_SOLUTION", releasedToParticipants: true })}
            >
              Lösung für Teilnehmer anzeigen
            </button>
            <button
              disabled={!projection.question}
              onClick={() => command({ type: "SHOW_SOLUTION", releasedToStream: true })}
            >
              Lösung für Stream anzeigen
            </button>
            <button disabled={!projection.question} onClick={onReset}>Frage zurücksetzen</button>
            {projection.fiftyFiftyAnimation && !fiftyFiftyComplete ? (
              <button onClick={() => command({ type: "SKIP_FIFTY_FIFTY_ANIMATION" })}>
                50:50 überspringen
              </button>
            ) : null}
          </div>
          <div data-toolbar-group="navigation">
            <span className="eyebrow">Navigation</span>
            <button onClick={() => command({ type: "UNDO" })}>Rückgängig</button>
          </div>
          <div data-toolbar-group="timer">
            <span className="eyebrow">Timer & Audio</span>
            <button
              disabled={!projection.question?.timerEnabled}
              onClick={() =>
                command({ type: "START_TIMER", durationSeconds: projection.question?.timerSeconds ?? 300 })
              }
            >
              Timer starten
            </button>
            <button onClick={() => command({ type: "STOP_TIMER" })}>Timer stoppen</button>
            {projection.question?.media.audioMediaId ? (
              <>
                <button
                  onClick={() =>
                    command({ type: "PLAY_AUDIO", mediaId: projection.question?.media.audioMediaId ?? "" })
                  }
                >
                  Audio starten
                </button>
                <button onClick={() => command({ type: "STOP_AUDIO" })}>Audio stoppen</button>
              </>
            ) : null}
          </div>
        </div>
      </details>
    </div>
  );
}
